import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isPackagingManufacturerSectionValid,
  PackagingManufacturerSection,
} from '@/shared/components/packaging/PackagingManufacturerSection'
import { PackagingPpwrComplianceFields } from '@/shared/components/packaging/PackagingPpwrComplianceFields'
import { PackagingVolumeCm3Field } from '@/shared/components/packaging/PackagingVolumeCm3Field'
import {
  formatPackagingVolumeCm3ForInput,
  isValidPackagingVolumeCm3Input,
  parsePackagingVolumeCm3Input,
} from '@/shared/components/packaging/componentNumericInput'
import type {
  PackagingItem,
  PackagingItemUpdatePayload,
  PackagingPpwrComplianceLevel,
  PackagingSupplier,
} from '@/shared/hooks/api/packagingSuppliers'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

type PackagingItemEditDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: PackagingItem
  supplier: PackagingSupplier | undefined
  isSaving: boolean
  onSave: (payload: PackagingItemUpdatePayload) => Promise<void>
}

export function PackagingItemEditDialog({
  open,
  onOpenChange,
  item,
  supplier,
  isSaving,
  onSave,
}: PackagingItemEditDialogProps) {
  const { t } = useTranslation('packagingSuppliers')

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [intendedUse, setIntendedUse] = useState('')
  const [sku, setSku] = useState('')
  const [packedItemSku, setPackedItemSku] = useState('')
  const [volumeCm3Input, setVolumeCm3Input] = useState('')
  const [supplierIsManufacturer, setSupplierIsManufacturer] = useState<
    boolean | undefined
  >(undefined)
  const [manufacturerName, setManufacturerName] = useState('')
  const [manufacturerAddress, setManufacturerAddress] = useState('')
  const [heavyMetalsStatus, setHeavyMetalsStatus] = useState<
    PackagingPpwrComplianceLevel | null | undefined
  >(undefined)
  const [substancesOfConcernStatus, setSubstancesOfConcernStatus] = useState<
    PackagingPpwrComplianceLevel | null | undefined
  >(undefined)
  const [saveFailed, setSaveFailed] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setName(item.name)
    setDescription(item.description)
    setIntendedUse(item.intendedUse)
    setSku(item.sku ?? '')
    setPackedItemSku(item.packedItemSku ?? '')
    setVolumeCm3Input(formatPackagingVolumeCm3ForInput(item.volumeCm3))
    setSupplierIsManufacturer(item.supplierIsManufacturer ?? undefined)
    setManufacturerName(item.manufacturerName ?? '')
    setManufacturerAddress(item.manufacturerAddress ?? '')
    setHeavyMetalsStatus(item.heavyMetalsStatus ?? null)
    setSubstancesOfConcernStatus(item.substancesOfConcernStatus ?? null)
    setSaveFailed(false)
  }, [item, open])

  const ppwrComplianceLabels = {
    heavyMetals: t('ppwrCompliance.heavyMetals'),
    substancesOfConcern: t('ppwrCompliance.substancesOfConcern'),
    none: t('ppwrCompliance.none'),
    sufficientlyMinimized: t('ppwrCompliance.sufficientlyMinimized'),
    heavyMetalsSufficientlyMinimizedHelp: t(
      'ppwrCompliance.heavyMetalsSufficientlyMinimizedHelp'
    ),
    notSufficientlyMinimized: t('ppwrCompliance.notSufficientlyMinimized'),
  }
  const manufacturerLabels = {
    question: t('items.manufacturer.question'),
    optionYes: t('items.manufacturer.optionYes'),
    optionNo: t('items.manufacturer.optionNo'),
    manufacturerName: t('items.manufacturer.manufacturerName'),
    manufacturerAddress: t('items.manufacturer.manufacturerAddress'),
    manufacturerNameRequired: t('items.manufacturer.manufacturerNameRequired'),
    manufacturerAddressRequired: t(
      'items.manufacturer.manufacturerAddressRequired'
    ),
  }

  const canSave =
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    intendedUse.trim().length > 0 &&
    sku.trim().length > 0 &&
    heavyMetalsStatus != null &&
    substancesOfConcernStatus != null &&
    isValidPackagingVolumeCm3Input(volumeCm3Input) &&
    isPackagingManufacturerSectionValid(
      supplierIsManufacturer,
      manufacturerName,
      manufacturerAddress
    )

  const buildPayload = (): PackagingItemUpdatePayload => {
    const payload: PackagingItemUpdatePayload = {
      name: name.trim(),
      description: description.trim(),
      intendedUse: intendedUse.trim(),
      sku: sku.trim(),
      packedItemSku: packedItemSku.trim() || null,
      volumeCm3: parsePackagingVolumeCm3Input(volumeCm3Input),
      heavyMetalsStatus: heavyMetalsStatus ?? undefined,
      substancesOfConcernStatus: substancesOfConcernStatus ?? undefined,
    }

    if (supplierIsManufacturer !== undefined) {
      payload.supplierIsManufacturer = supplierIsManufacturer
      payload.manufacturerName = manufacturerName.trim()
      payload.manufacturerAddress = manufacturerAddress.trim()
    }

    return payload
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>{t('itemDetail.editDetails')}</DialogTitle>
        </DialogHeader>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label htmlFor='edit-item-name'>{t('items.form.name')}</Label>
              <Input
                id='edit-item-name'
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='edit-item-description'>
                {t('items.form.description')}
              </Label>
              <Textarea
                id='edit-item-description'
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className='space-y-1'>
            <Label htmlFor='edit-item-intended-use'>
              {t('items.form.intendedUse')}
            </Label>
            <Textarea
              id='edit-item-intended-use'
              value={intendedUse}
              onChange={(event) => setIntendedUse(event.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className='grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2'>
            <div className='space-y-1'>
              <Label htmlFor='edit-item-sku'>{t('items.form.sku')}</Label>
              <Input
                id='edit-item-sku'
                value={sku}
                onChange={(event) => setSku(event.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className='space-y-1 opacity-60'>
              <Label htmlFor='edit-item-packed-item-sku'>
                {t('items.form.packedItemSku')}
              </Label>
              <Input
                id='edit-item-packed-item-sku'
                value={packedItemSku}
                onChange={(event) => setPackedItemSku(event.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className='sm:col-span-2'>
            <PackagingVolumeCm3Field
              id='edit-item-volume-cm3'
              value={volumeCm3Input}
              onChange={setVolumeCm3Input}
              disabled={isSaving}
              labels={{
                label: t('items.form.volumeCm3'),
                importantNoteTitle: t('items.form.volumeCm3ImportantNoteTitle'),
                importantNoteBody: t('items.form.volumeCm3ImportantNoteBody'),
                invalidMessage: t('items.form.volumeCm3Invalid'),
              }}
            />
          </div>
          <div className='sm:col-span-2'>
            <PackagingManufacturerSection
              supplierIsManufacturer={supplierIsManufacturer}
              onSupplierIsManufacturerChange={setSupplierIsManufacturer}
              manufacturerName={manufacturerName}
              onManufacturerNameChange={setManufacturerName}
              manufacturerAddress={manufacturerAddress}
              onManufacturerAddressChange={setManufacturerAddress}
              supplierName={supplier?.name ?? ''}
              supplierAddress={supplier?.address ?? ''}
              labels={manufacturerLabels}
              disabled={isSaving}
            />
          </div>
          <div className='grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2'>
            <PackagingPpwrComplianceFields
              heavyMetalsStatus={heavyMetalsStatus}
              substancesOfConcernStatus={substancesOfConcernStatus}
              onHeavyMetalsChange={setHeavyMetalsStatus}
              onSubstancesChange={setSubstancesOfConcernStatus}
              showSubstancesOfConcern={false}
              labels={ppwrComplianceLabels}
              disabled={isSaving}
            />
            <PackagingPpwrComplianceFields
              heavyMetalsStatus={heavyMetalsStatus}
              substancesOfConcernStatus={substancesOfConcernStatus}
              onHeavyMetalsChange={setHeavyMetalsStatus}
              onSubstancesChange={setSubstancesOfConcernStatus}
              showHeavyMetals={false}
              labels={ppwrComplianceLabels}
              disabled={isSaving}
            />
          </div>
        </div>
        <DialogFooter className='gap-2 sm:gap-0'>
          {saveFailed && (
            <p className='me-auto text-sm text-destructive'>
              {t('itemDetail.saveFailed')}
            </p>
          )}
          <Button
            type='button'
            variant='outline'
            disabled={isSaving}
            onClick={() => onOpenChange(false)}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type='button'
            disabled={isSaving || !canSave}
            onClick={() => {
              setSaveFailed(false)
              void onSave(buildPayload()).catch(() => {
                setSaveFailed(true)
              })
            }}
          >
            {isSaving ? t('itemDetail.saving') : t('itemDetail.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
